"use client";

import { useEffect, useId } from "react";

import {
  INSEE_LOCKED_FIELD_HINT,
  PartyProfileFields,
  ProfileFieldLabel,
  ProfileTextInput,
  type ProfileFieldsValue,
} from "@/components/backoffice/cat/vehicle-assignment/client-step/party-profile-fields";
import { Card, CardContent } from "@/components/ui/card";
import { Field, FieldContent, FieldError } from "@/components/ui/field";
import type { EditableCompanyProfile } from "@/lib/cat/company-profile.types";
import { isCompanyEditComplete } from "@/lib/cat/draft-client-mapper";
import type { CompanyPrefilledFieldLocks } from "@/lib/cat/mdm-map-company";
import { validateCompanyProfileFields } from "@/lib/cat/party-profile-validation";

type LockedFields = Partial<Record<keyof ProfileFieldsValue, boolean>>;

function CompanyCreateForm({
  value,
  onChange,
  onCanProceedChange,
  prefilledLockedFields,
  title = "Création dans le Référentiel Personne (RefPers)",
  description = "Les champs préremplis depuis l'INSEE sont en lecture seule. Complétez les contacts et les compléments d'adresse manquants si nécessaire.",
}: {
  value: EditableCompanyProfile;
  onChange: (value: EditableCompanyProfile) => void;
  onCanProceedChange?: (canProceed: boolean) => void;
  prefilledLockedFields: CompanyPrefilledFieldLocks;
  title?: string;
  description?: string;
}) {
  const idPrefix = useId();
  const locked: LockedFields & {
    companyDenomination: boolean;
    apeCode: boolean;
  } = {
    companyDenomination: prefilledLockedFields.companyDenomination,
    apeCode: prefilledLockedFields.apeCode,
    email: false,
    addressLine: prefilledLockedFields.addressLine,
    addressFloorApartment: prefilledLockedFields.addressFloorApartment,
    addressBuildingResidence: prefilledLockedFields.addressBuildingResidence,
    postBox: prefilledLockedFields.postBox,
    postalCode: prefilledLockedFields.postalCode,
    city: prefilledLockedFields.city,
    fixedPhone: false,
    mobilePhone: false,
  };
  const canProceed = isCompanyEditComplete(value);
  const fieldErrors = validateCompanyProfileFields(value);

  useEffect(() => {
    onCanProceedChange?.(canProceed);
  }, [canProceed, onCanProceedChange]);

  const patchProfile = (partial: Partial<EditableCompanyProfile>) =>
    onChange({ ...value, ...partial });

  return (
    <Card className="border-muted">
      <CardContent className="space-y-4 pt-6">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-muted-foreground text-sm">
          {description} Les champs marqués d&apos;un{" "}
          <span className="text-destructive">*</span> sont obligatoires.
        </p>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field
            data-invalid={fieldErrors.companyDenomination ? true : undefined}
          >
            <ProfileFieldLabel
              htmlFor={`${idPrefix}-denomination`}
              label="Raison sociale"
              locked={locked.companyDenomination}
              lockedHint={INSEE_LOCKED_FIELD_HINT}
              required
            />
            <FieldContent>
              <ProfileTextInput
                id={`${idPrefix}-denomination`}
                locked={locked.companyDenomination}
                invalid={Boolean(fieldErrors.companyDenomination)}
                value={value.companyDenomination}
                onChange={(companyDenomination) =>
                  patchProfile({ companyDenomination })
                }
              />
              {fieldErrors.companyDenomination ? (
                <FieldError>{fieldErrors.companyDenomination}</FieldError>
              ) : null}
            </FieldContent>
          </Field>
          <Field data-invalid={fieldErrors.apeCode ? true : undefined}>
            <ProfileFieldLabel
              htmlFor={`${idPrefix}-ape`}
              label="Code APE"
              locked={locked.apeCode}
              lockedHint={INSEE_LOCKED_FIELD_HINT}
              required
            />
            <FieldContent>
              <ProfileTextInput
                id={`${idPrefix}-ape`}
                locked={locked.apeCode}
                invalid={Boolean(fieldErrors.apeCode)}
                value={value.apeCode}
                onChange={(apeCode) => patchProfile({ apeCode })}
              />
              {fieldErrors.apeCode ? (
                <FieldError>{fieldErrors.apeCode}</FieldError>
              ) : null}
            </FieldContent>
          </Field>
        </div>

        <PartyProfileFields
          idPrefix={`${idPrefix}-party`}
          value={value}
          onChange={(party) => onChange({ ...value, ...party })}
          lockedFields={locked}
          lockedFieldHint={INSEE_LOCKED_FIELD_HINT}
          showValidation
          requireEmail={false}
        />
      </CardContent>
    </Card>
  );
}

export { CompanyCreateForm };
